import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  CreditCard,
  DollarSign,
  FileText,
  Download,
  Calendar,
  TrendingUp,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  Shield
} from 'lucide-react';

interface BillingData {
  currentPlan: {
    name: string;
    price: number;
    billingCycle: 'monthly' | 'annual';
    nextBillingDate: string;
    features: string[];
  };
  usage: {
    users: number;
    maxUsers: number;
    storage: number;
    maxStorage: number;
    apiCalls: number;
    maxApiCalls: number;
  };
  paymentMethod: {
    type: string;
    last4: string;
    expiryDate: string;
  };
  invoices: Array<{
    id: string;
    date: string;
    amount: number;
    status: 'paid' | 'pending' | 'overdue';
    downloadUrl: string;
  }>;
}

const Billing: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAccountManager } = useAuth();
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'usage' | 'invoices'>('overview');

  useEffect(() => {
    // Check if user has Account_Manager role
    if (!isAccountManager) {
      navigate('/dashboard');
      return;
    }

    fetchBillingData();
  }, [isAccountManager, navigate]);

  const fetchBillingData = async () => {
    try {
      // This would be replaced with actual API call
      // const response = await fetch('/api/billing', {
      //   headers: { Authorization: `Bearer ${await getIdToken()}` }
      // });
      // const data = await response.json();
      
      // Mock data for now
      setBillingData({
        currentPlan: {
          name: 'Professional',
          price: 299,
          billingCycle: 'monthly',
          nextBillingDate: '2025-10-13',
          features: [
            'Unlimited Digital Twins',
            'Advanced Analytics',
            'Priority Support',
            'API Access',
            'Custom Integrations'
          ]
        },
        usage: {
          users: 25,
          maxUsers: 50,
          storage: 120,
          maxStorage: 500,
          apiCalls: 45000,
          maxApiCalls: 100000
        },
        paymentMethod: {
          type: 'visa',
          last4: '4242',
          expiryDate: '12/25'
        },
        invoices: [
          {
            id: 'INV-2025-09',
            date: '2025-09-01',
            amount: 299,
            status: 'paid',
            downloadUrl: '#'
          },
          {
            id: 'INV-2025-08',
            date: '2025-08-01',
            amount: 299,
            status: 'paid',
            downloadUrl: '#'
          },
          {
            id: 'INV-2025-07',
            date: '2025-07-01',
            amount: 299,
            status: 'paid',
            downloadUrl: '#'
          }
        ]
      });
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getUsagePercentage = (current: number, max: number) => {
    return Math.round((current / max) * 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage < 50) return 'bg-green-500';
    if (percentage < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-700"></div>
      </div>
    );
  }

  if (!billingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Unable to load billing information</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Subscription</h1>
        <p className="text-gray-600">Manage your subscription, usage, and payment methods</p>
      </div>

      {/* Account Manager Badge */}
      <div className="mb-6 inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 rounded-lg">
        <Shield className="w-4 h-4 mr-2" />
        <span className="text-sm font-medium">Account Manager Access</span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {(['overview', 'usage', 'invoices'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                activeTab === tab
                  ? 'border-purple-700 text-purple-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Plan */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Current Plan</h2>
              <button className="px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 transition-colors">
                Upgrade Plan
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-baseline mb-2">
                <span className="text-3xl font-bold text-gray-900">
                  {billingData.currentPlan.name}
                </span>
                <span className="ml-2 text-gray-600">Plan</span>
              </div>
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-gray-900">
                  {formatCurrency(billingData.currentPlan.price)}
                </span>
                <span className="ml-2 text-gray-600">/{billingData.currentPlan.billingCycle}</span>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-semibold text-gray-900 mb-4">Plan Features</h3>
              <ul className="space-y-3">
                {billingData.currentPlan.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Next billing date</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {new Date(billingData.currentPlan.nextBillingDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Payment Method</h2>
            
            <div className="border rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <CreditCard className="w-8 h-8 text-gray-400 mr-3" />
                  <div>
                    <p className="font-semibold text-gray-900 capitalize">
                      {billingData.paymentMethod.type} •••• {billingData.paymentMethod.last4}
                    </p>
                    <p className="text-sm text-gray-600">
                      Expires {billingData.paymentMethod.expiryDate}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              Update Payment Method
            </button>
          </div>
        </div>
      )}

      {activeTab === 'usage' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Users */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-purple-700 mr-3" />
                <div>
                  <h3 className="font-semibold text-gray-900">Users</h3>
                  <p className="text-sm text-gray-600">
                    {billingData.usage.users} of {billingData.usage.maxUsers}
                  </p>
                </div>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {getUsagePercentage(billingData.usage.users, billingData.usage.maxUsers)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getUsageColor(
                  getUsagePercentage(billingData.usage.users, billingData.usage.maxUsers)
                )}`}
                style={{
                  width: `${getUsagePercentage(billingData.usage.users, billingData.usage.maxUsers)}%`
                }}
              />
            </div>
          </div>

          {/* Storage */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-purple-700 mr-3" />
                <div>
                  <h3 className="font-semibold text-gray-900">Storage</h3>
                  <p className="text-sm text-gray-600">
                    {billingData.usage.storage} GB of {billingData.usage.maxStorage} GB
                  </p>
                </div>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {getUsagePercentage(billingData.usage.storage, billingData.usage.maxStorage)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getUsageColor(
                  getUsagePercentage(billingData.usage.storage, billingData.usage.maxStorage)
                )}`}
                style={{
                  width: `${getUsagePercentage(billingData.usage.storage, billingData.usage.maxStorage)}%`
                }}
              />
            </div>
          </div>

          {/* API Calls */}
          <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <TrendingUp className="w-8 h-8 text-purple-700 mr-3" />
                <div>
                  <h3 className="font-semibold text-gray-900">API Calls</h3>
                  <p className="text-sm text-gray-600">
                    {billingData.usage.apiCalls.toLocaleString()} of {billingData.usage.maxApiCalls.toLocaleString()}
                  </p>
                </div>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {getUsagePercentage(billingData.usage.apiCalls, billingData.usage.maxApiCalls)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getUsageColor(
                  getUsagePercentage(billingData.usage.apiCalls, billingData.usage.maxApiCalls)
                )}`}
                style={{
                  width: `${getUsagePercentage(billingData.usage.apiCalls, billingData.usage.maxApiCalls)}%`
                }}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Invoice History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {billingData.invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invoice.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(invoice.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : invoice.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {invoice.status === 'paid' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {invoice.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                        {invoice.status === 'overdue' && <AlertCircle className="w-3 h-3 mr-1" />}
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button className="text-purple-700 hover:text-purple-800 flex items-center">
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;